// Geometrie-Helfer für den pvXpert-Dacheditor.
//
// Architektur (siehe Handoff): MapLibre bleibt der geo-referenzierte Hintergrund.
// Die Zeichenebene ist ein SVG-Overlay über der Karte. Quelle der Wahrheit pro
// Objekt ist `polygon: LatLng[]`. Für das Rendern/Interagieren projizieren wir
// pro Frame mit `map.project()` nach Bildschirm-Pixeln; Bearbeitungen kommen via
// `map.unproject()` zurück. Transformationen (Skalieren/Drehen/Verschieben)
// rechnen wir in einem lokalen Meter-Raster um den Zentroid — zoom-unabhängig.

export interface LatLng {
  lat: number;
  lng: number;
}

export interface Pt {
  x: number;
  y: number;
}

const R = 6371000; // Erdradius [m]
const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

// ── Fläche (Gauß'sche Formel auf WGS84-Näherung) — identisch zu RoofDrawMap ──
export function areaM2(coords: LatLng[]): number {
  if (coords.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length;
    const ci = coords[i];
    const cj = coords[j];
    if (!ci || !cj) continue;
    const xi = toRad(ci.lng) * Math.cos(toRad(ci.lat)) * R;
    const yi = toRad(ci.lat) * R;
    const xj = toRad(cj.lng) * Math.cos(toRad(cj.lat)) * R;
    const yj = toRad(cj.lat) * R;
    area += xi * yj - xj * yi;
  }
  return Math.abs(area / 2);
}

// ── Haversine-Kantenlänge [m] ──
export function edgeLengthM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

// ── Lokale Meter-Projektion (gleiche Konvention wie areaM2) ──
// LatLng → ebene (x,y)-Meter, verschoben auf den ersten Punkt als lokalen
// Ursprung. Für planare Geometrie-Prüfungen (z. B. isSimplePolygon/
// Selbstschneidung). Zentrierung nahe (0,0) statt absoluter ~5,2-Mio.-m-Werte
// verbessert die Fließkomma-Konditionierung; die Topologie bleibt erhalten.
export function toLocalMetersXY(coords: LatLng[]): Pt[] {
  if (coords.length === 0) return [];
  const lat0 = toRad(coords[0]!.lat);
  const ox = toRad(coords[0]!.lng) * Math.cos(lat0) * R;
  const oy = lat0 * R;
  return coords.map((c) => ({
    x: toRad(c.lng) * Math.cos(toRad(c.lat)) * R - ox,
    y: toRad(c.lat) * R - oy,
  }));
}

// ── Zentroid (arithmetisches Mittel der Eckpunkte) ──
export function centroidLL(coords: LatLng[]): LatLng {
  const n = coords.length || 1;
  return {
    lat: coords.reduce((a, c) => a + c.lat, 0) / n,
    lng: coords.reduce((a, c) => a + c.lng, 0) / n,
  };
}

/** Gemeinsamer Zentroid mehrerer Polygone (alle Eckpunkte gleich gewichtet). */
export function centroidOfAll(polys: LatLng[][]): LatLng | null {
  const all = polys.flat();
  if (all.length === 0) return null;
  return centroidLL(all);
}

// ── Lokales Meter-Raster um einen Bezugspunkt c (equirektangulär) ──
function toLocal(p: LatLng, c: LatLng): Pt {
  return {
    x: toRad(p.lng - c.lng) * Math.cos(toRad(c.lat)) * R,
    y: toRad(p.lat - c.lat) * R,
  };
}
function fromLocal(pt: Pt, c: LatLng): LatLng {
  return {
    lat: c.lat + toDeg(pt.y / R),
    lng: c.lng + toDeg(pt.x / (Math.cos(toRad(c.lat)) * R)),
  };
}

/** Skaliert ein Polygon um den Faktor k um seinen (oder einen vorgegebenen) Zentroid. */
export function scaleAbout(coords: LatLng[], k: number, c?: LatLng): LatLng[] {
  const center = c ?? centroidLL(coords);
  return coords.map((p) => {
    const l = toLocal(p, center);
    return fromLocal({ x: l.x * k, y: l.y * k }, center);
  });
}

/** Dreht ein Polygon um `deg` Grad (math. positiv) um seinen (oder vorgegebenen) Zentroid. */
export function rotateAbout(coords: LatLng[], deg: number, c?: LatLng): LatLng[] {
  const center = c ?? centroidLL(coords);
  const th = toRad(deg);
  const co = Math.cos(th);
  const si = Math.sin(th);
  return coords.map((p) => {
    const l = toLocal(p, center);
    return fromLocal({ x: l.x * co - l.y * si, y: l.x * si + l.y * co }, center);
  });
}

/** Verschiebt ein Polygon um einen Meter-Vektor (dx = Ost, dy = Nord). */
export function translateByMeters(coords: LatLng[], dx: number, dy: number): LatLng[] {
  if (coords.length === 0) return coords;
  const c = centroidLL(coords);
  return coords.map((p) => {
    const l = toLocal(p, c);
    return fromLocal({ x: l.x + dx, y: l.y + dy }, c);
  });
}

/** Verschiebt ein Polygon um einen LatLng-Delta (für „An Standort"). */
export function translateByLL(coords: LatLng[], dLat: number, dLng: number): LatLng[] {
  return coords.map((p) => ({ lat: p.lat + dLat, lng: p.lng + dLng }));
}

// ── Punkt-in-Polygon (Ray-Casting) in Bildschirm-Pixeln ──
export function pointInPoly(pts: Pt[], p: Pt): boolean {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const pi = pts[i];
    const pj = pts[j];
    if (!pi || !pj) continue;
    if (
      pi.y > p.y !== pj.y > p.y &&
      p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x
    ) {
      c = !c;
    }
  }
  return c;
}

export function distPx(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Kürzester Abstand (px) eines Punkts p zur Strecke a–b (für Kanten-Treffer). */
export function distToSegmentPx(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return distPx(p, a);
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return distPx(p, { x: a.x + t * dx, y: a.y + t * dy });
}

/** Mittelpunkt zweier Bildschirm-Punkte. */
export function midPt(a: Pt, b: Pt): Pt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Bounding-Box mehrerer LatLng-Polygone → [[minLng,minLat],[maxLng,maxLat]] oder null. */
export function bboxOf(polys: LatLng[][]): [[number, number], [number, number]] | null {
  const all = polys.flat();
  if (all.length === 0) return null;
  const lats = all.map((p) => p.lat);
  const lngs = all.map((p) => p.lng);
  return [
    [Math.min(...lngs), Math.min(...lats)],
    [Math.max(...lngs), Math.max(...lats)],
  ];
}
